import type { SubscriptionsSku } from '@calm-web/api-sdk/lib/atlas-subscriptions-api';

import type { Coupon, Coupons, PriceOptions, Prices } from '@/store/prices/types';
import type { OneTimePurchasePlanCadence, PlanCadence, PurchaseParams } from '@/store/purchaseParams/types';

export const durationToPlanCadence = (
	duration: string | null | undefined,
): Exclude<PlanCadence, 'family'> => {
	let planCadence: PlanCadence = 'yearly';
	if (duration === 'year') {
		planCadence = 'yearly';
	} else if (duration === 'month') {
		planCadence = 'monthly';
	} else if (duration === '3_months') {
		planCadence = 'quarterly';
	} else if (duration === 'lifetime') {
		planCadence = 'lifetime';
	}
	return planCadence;
};

export const planCadenceToDuration = (
	planCadence: PlanCadence | OneTimePurchasePlanCadence,
): string | null => {
	switch (planCadence) {
		case 'yearly':
			return 'year';
		case 'monthly':
			return 'month';
		case 'quarterly':
			return '3_months';
		case 'lifetime':
			return 'lifetime';
		default:
			return null;
	}
};

const getPriceOptions = (price: number, country: string, currency: string): PriceOptions => {
	price /= 10;
	const priceOptions: PriceOptions = {
		monthly: price,
		yearly: price,
		quarterly: price,
		lifetime: price,
		family: price,
		currency,
		country,
	};
	return priceOptions;
};

export const getPurchaseParams = ({ duration }: Pick<SubscriptionsSku, 'duration'>): PurchaseParams => ({
	plan: durationToPlanCadence(duration),
});

export const getPrices = ({
	basePrice,
	country,
	currency,
	discount,
	duration,
	finalPrice,
}: Pick<
	SubscriptionsSku,
	'basePrice' | 'country' | 'currency' | 'discount' | 'duration' | 'finalPrice'
>): Prices => {
	const original = getPriceOptions(basePrice, country, currency);
	const current = getPriceOptions(finalPrice, country, currency);

	const planCadence = durationToPlanCadence(duration);

	const coupons: Coupons = {};

	if (typeof discount === 'string') {
		const coupon: Coupon = {
			id: '',
			duration: '',
			valid: true,
		};

		if (discount.endsWith('%')) {
			const [percentOff] = discount.split('%');
			if (percentOff) {
				coupon.percent_off = Number(percentOff);
			}
		}

		coupons[planCadence] = coupon;
	}

	return {
		current,
		original,
		coupons,
		pricing_format: { format: '$%PRICE% %CURRENCY%', seperator: '.', currency },
	};
};
